import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import api from "@/lib/api";
import {
  ArrowLeft, User, MapPin, Heart, Calendar, FileText,
  Pill, Activity, Plus, Clock, Stethoscope, Users, Trash2, Copy, Check, X,
  Bath, UtensilsCrossed, Droplets, PersonStanding, Moon, Smile, Pencil
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const depColors = {
  autovalido: { bg: "bg-emerald-50", text: "text-emerald-700", label: "Autoválido" },
  dependencia_media: { bg: "bg-amber-50", text: "text-amber-700", label: "Dependencia Media" },
  dependencia_total: { bg: "bg-rose-50", text: "text-rose-600", label: "Dependencia Total" },
};

const katzItems = [
  { key: "bathing", label: "Baño" }, { key: "dressing", label: "Vestido" },
  { key: "toileting", label: "Uso de retrete" }, { key: "transferring", label: "Movilización" },
  { key: "continence", label: "Continencia" }, { key: "feeding", label: "Alimentación" },
];

const lawtonItems = [
  { key: "telephone", label: "Teléfono" }, { key: "shopping", label: "Compras" },
  { key: "food_prep", label: "Preparar comida" }, { key: "housekeeping", label: "Cuidado del hogar" },
  { key: "laundry", label: "Lavado de ropa" }, { key: "transportation", label: "Transporte" },
  { key: "medication", label: "Medicación" }, { key: "finances", label: "Finanzas" },
];

const CARE_ITEMS = [
  { key: "desayuno", label: "Desayuno", icon: UtensilsCrossed, color: "text-amber-600 bg-amber-50" },
  { key: "almuerzo", label: "Almuerzo", icon: UtensilsCrossed, color: "text-orange-600 bg-orange-50" },
  { key: "merienda", label: "Merienda", icon: UtensilsCrossed, color: "text-yellow-600 bg-yellow-50" },
  { key: "cena", label: "Cena", icon: UtensilsCrossed, color: "text-rose-600 bg-rose-50" },
  { key: "hidratacion", label: "Hidratación", icon: Droplets, color: "text-sky-600 bg-sky-50" },
  { key: "bano", label: "Baño", icon: Bath, color: "text-cyan-600 bg-cyan-50" },
  { key: "higiene_personal", label: "Higiene Personal", icon: Check, color: "text-teal-600 bg-teal-50" },
  { key: "movilizacion", label: "Movilización", icon: PersonStanding, color: "text-emerald-600 bg-emerald-50" },
  { key: "descanso_nocturno", label: "Descanso Nocturno", icon: Moon, color: "text-indigo-600 bg-indigo-50" },
];

const MOODS = [
  { key: "bueno", label: "Bueno", emoji: "Bueno" },
  { key: "regular", label: "Regular", emoji: "Regular" },
  { key: "malo", label: "Decaído", emoji: "Decaído" },
];

function DailyCareChecklist({ residentId, todayStr, dailyCare, onUpdate, canManage }) {
  const [shiftType, setShiftType] = useState(() => {
    const h = new Date().getHours();
    return h >= 6 && h < 14 ? "manana" : h >= 14 && h < 22 ? "tarde" : "noche";
  });
  const [saving, setSaving] = useState(false);

  const currentRecord = dailyCare.find(r => r.shift_type === shiftType) || null;
  const currentItems = currentRecord?.items || {};
  const currentMood = currentRecord?.mood || "";
  const currentNotes = currentRecord?.notes || "";
  const [localNotes, setLocalNotes] = useState(currentNotes);

  const isChecked = (key) => !!currentItems[key];

  const toggleItem = async (key) => {
    if (!canManage) return;
    setSaving(true);
    try {
      const newVal = !isChecked(key);
      await api.post("/daily-care", {
        resident_id: residentId,
        date: todayStr,
        shift_type: shiftType,
        items: { [key]: newVal },
      });
      const r = await api.get("/daily-care", { params: { resident_id: residentId, date: todayStr } });
      onUpdate(r.data);
    } catch { toast.error("Error al guardar"); }
    setSaving(false);
  };

  const setMood = async (m) => {
    if (!canManage) return;
    try {
      await api.post("/daily-care", {
        resident_id: residentId,
        date: todayStr,
        shift_type: shiftType,
        mood: m,
        items: {},
      });
      const r = await api.get("/daily-care", { params: { resident_id: residentId, date: todayStr } });
      onUpdate(r.data);
    } catch { toast.error("Error"); }
  };

  const saveNotes = async () => {
    if (!canManage) return;
    try {
      await api.post("/daily-care", {
        resident_id: residentId,
        date: todayStr,
        shift_type: shiftType,
        notes: localNotes,
        items: {},
      });
      const r = await api.get("/daily-care", { params: { resident_id: residentId, date: todayStr } });
      onUpdate(r.data);
      toast.success("Notas guardadas");
    } catch { toast.error("Error"); }
  };

  const completedCount = CARE_ITEMS.filter(i => isChecked(i.key)).length;
  const shiftLabels = { manana: "Mañana", tarde: "Tarde", noche: "Noche" };

  return (
    <div className="space-y-4" data-testid="daily-care-checklist">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="font-semibold text-slate-900 text-sm">Checklist del Día — {todayStr}</h3>
          <p className="text-xs text-slate-400 mt-0.5">{completedCount}/{CARE_ITEMS.length} actividades registradas</p>
        </div>
        <div className="flex gap-1.5">
          {["manana", "tarde", "noche"].map(s => (
            <button
              key={s}
              onClick={() => setShiftType(s)}
              className={`text-xs font-semibold px-3.5 py-2 rounded-lg transition-all ${shiftType === s ? "bg-violet-600 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
              data-testid={`care-shift-${s}`}
            >
              {shiftLabels[s]}
            </button>
          ))}
        </div>
      </div>

      <Card className="rounded-2xl border-slate-100 shadow-sm">
        <CardContent className="p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {CARE_ITEMS.map((item) => {
              const checked = isChecked(item.key);
              const Icon = item.icon;
              return (
                <button
                  key={item.key}
                  onClick={() => toggleItem(item.key)}
                  disabled={!canManage || saving}
                  className={`flex items-center gap-3 rounded-xl p-3.5 border transition-all text-left ${
                    checked
                      ? "bg-emerald-50 border-emerald-200"
                      : "bg-white border-slate-100 hover:border-slate-200"
                  } ${canManage ? "cursor-pointer active:scale-[0.98]" : "cursor-default"}`}
                  data-testid={`care-item-${item.key}`}
                >
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${checked ? "bg-emerald-100" : item.color}`}>
                    {checked ? <Check className="w-4 h-4 text-emerald-600" /> : <Icon className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${checked ? "text-emerald-700" : "text-slate-700"}`}>{item.label}</p>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    checked ? "border-emerald-500 bg-emerald-500" : "border-slate-300"
                  }`}>
                    {checked && <Check className="w-3 h-3 text-white" />}
                  </div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Mood */}
      <Card className="rounded-2xl border-slate-100 shadow-sm">
        <CardContent className="p-5">
          <div className="flex items-center gap-3 mb-3">
            <Smile className="w-4 h-4 text-slate-400" />
            <h4 className="font-semibold text-slate-900 text-sm">Estado de Ánimo</h4>
          </div>
          <div className="flex gap-2">
            {MOODS.map(m => (
              <button
                key={m.key}
                onClick={() => setMood(m.key)}
                disabled={!canManage}
                className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                  currentMood === m.key
                    ? m.key === "bueno" ? "bg-emerald-100 text-emerald-700 border border-emerald-200"
                      : m.key === "regular" ? "bg-amber-100 text-amber-700 border border-amber-200"
                      : "bg-rose-100 text-rose-700 border border-rose-200"
                    : "bg-slate-50 text-slate-500 border border-slate-100 hover:bg-slate-100"
                }`}
                data-testid={`care-mood-${m.key}`}
              >
                {m.label}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Notes */}
      {canManage && (
        <Card className="rounded-2xl border-slate-100 shadow-sm">
          <CardContent className="p-5">
            <h4 className="font-semibold text-slate-900 text-sm mb-2">Observaciones del turno</h4>
            <Textarea
              value={localNotes}
              onChange={e => setLocalNotes(e.target.value)}
              placeholder="Ej: Se alimentó bien, rechazó postre. Se bañó con asistencia."
              className="rounded-xl bg-slate-50 min-h-[70px] border-slate-200 text-sm"
              data-testid="care-notes-input"
            />
            <Button
              onClick={saveNotes}
              className="mt-2 rounded-xl h-9 px-5 bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700"
              data-testid="care-save-notes"
            >
              Guardar observaciones
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Other shifts summary */}
      {dailyCare.filter(r => r.shift_type !== shiftType).length > 0 && (
        <div className="space-y-3">
          <h4 className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Otros turnos de hoy</h4>
          {dailyCare.filter(r => r.shift_type !== shiftType).map(rec => {
            const done = Object.values(rec.items || {}).filter(Boolean).length;
            return (
              <Card key={rec.id} className="rounded-2xl border-slate-100 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Badge className="rounded-full bg-slate-100 text-slate-600 border-0 text-[10px] font-semibold">{shiftLabels[rec.shift_type]}</Badge>
                      <p className="text-xs text-slate-400 mt-1">{done}/{CARE_ITEMS.length} completados — {rec.created_by_name}</p>
                    </div>
                    {rec.mood && (
                      <Badge className={`rounded-full text-[10px] font-semibold border-0 ${rec.mood === "bueno" ? "bg-emerald-50 text-emerald-700" : rec.mood === "regular" ? "bg-amber-50 text-amber-700" : "bg-rose-50 text-rose-600"}`}>
                        Ánimo: {rec.mood}
                      </Badge>
                    )}
                  </div>
                  {rec.notes && <p className="text-xs text-slate-500 mt-2">{rec.notes}</p>}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function ResidentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = user?.role === "super_admin" || user?.role === "tenant_admin" || user?.permissions?.includes("editar_pacientes");
  const [resident, setResident] = useState(null);
  const [prescriptions, setPrescriptions] = useState([]);
  const [vitals, setVitals] = useState([]);
  const [evolutions, setEvolutions] = useState([]);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rxDialog, setRxDialog] = useState(false);
  const [evoDialog, setEvoDialog] = useState(false);
  const [famDialog, setFamDialog] = useState(false);
  const [rxForm, setRxForm] = useState({ medication_name: "", dosage: "", route: "oral", type: "cronico", scheduled_times: "08:00,20:00", notes: "", prescribing_doctor: "" });
  const [evoForm, setEvoForm] = useState({ content: "", category: "general" });
  const [famForm, setFamForm] = useState({ name: "", email: "", phone: "", relationship: "hijo" });
  const [newFamCredentials, setNewFamCredentials] = useState(null);
  // Historia Clínica
  const [diagnoses, setDiagnoses] = useState([]);
  const [allergies, setAllergies] = useState([]);
  const [surgicalHistory, setSurgicalHistory] = useState([]);
  const [residentDoctors, setResidentDoctors] = useState([]);
  const [allDoctors, setAllDoctors] = useState([]);
  // Daily Care
  const [dailyCare, setDailyCare] = useState([]);
  const [careLoading, setCareLoading] = useState(false);
  const todayStr = new Date().toISOString().split("T")[0];
  const [hcDialog, setHcDialog] = useState(null);
  const [editDialog, setEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({}); // "diagnosis", "allergy", "surgery", "doctor"
  const [diagForm, setDiagForm] = useState({ name: "", icd_code: "", diagnosis_date: "", status: "activo", notes: "" });
  const [allergyForm, setAllergyForm] = useState({ allergen: "", allergy_type: "medicamentosa", severity: "moderada", reaction: "", notes: "" });
  const [surgeryForm, setSurgeryForm] = useState({ procedure: "", date: "", hospital: "", surgeon: "", notes: "" });
  const [linkDoctorForm, setLinkDoctorForm] = useState({ doctor_id: "", role: "especialista" });

  useEffect(() => {
    const load = async () => {
      try {
        const [rRes, rxRes, vRes, evoRes, famRes, diagRes, allergRes, surgRes, docRes, allDocRes] = await Promise.all([
          api.get(`/residents/${id}`),
          api.get("/prescriptions", { params: { resident_id: id } }),
          api.get("/vitals", { params: { resident_id: id } }),
          api.get("/evolutions", { params: { resident_id: id } }),
          api.get(`/residents/${id}/family`).catch(() => ({ data: [] })),
          api.get(`/residents/${id}/diagnoses`).catch(() => ({ data: [] })),
          api.get(`/residents/${id}/allergies`).catch(() => ({ data: [] })),
          api.get(`/residents/${id}/surgical-history`).catch(() => ({ data: [] })),
          api.get(`/residents/${id}/doctors`).catch(() => ({ data: [] })),
          api.get("/doctors").catch(() => ({ data: [] })),
        ]);
        setResident(rRes.data);
        setPrescriptions(rxRes.data);
        setVitals(vRes.data);
        setEvolutions(evoRes.data);
        setFamilyMembers(famRes.data);
        setDiagnoses(diagRes.data);
        setAllergies(allergRes.data);
        setSurgicalHistory(surgRes.data);
        setResidentDoctors(docRes.data);
        setAllDoctors(allDocRes.data);
        // Load daily care
        try {
          const dcRes = await api.get("/daily-care", { params: { resident_id: id, date: new Date().toISOString().split("T")[0] } });
          setDailyCare(dcRes.data);
        } catch { /* ignore */ }
      } catch { toast.error("Error al cargar datos"); }
      setLoading(false);
    };
    load();
  }, [id]);

  const handleAddRx = async (e) => {
    e.preventDefault();
    try {
      await api.post("/prescriptions", {
        ...rxForm, resident_id: id,
        scheduled_times: rxForm.scheduled_times.split(",").map(t => t.trim()).filter(Boolean),
        frequency_hours: rxForm.type === "sos" ? 0 : 8,
      });
      toast.success("Prescripción creada");
      setRxDialog(false);
      const r = await api.get("/prescriptions", { params: { resident_id: id } });
      setPrescriptions(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handleAddEvolution = async (e) => {
    e.preventDefault();
    try {
      await api.post("/evolutions", { ...evoForm, resident_id: id });
      toast.success("Evolución registrada");
      setEvoDialog(false);
      setEvoForm({ content: "", category: "general" });
      const r = await api.get("/evolutions", { params: { resident_id: id } });
      setEvolutions(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handleAddFamily = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post(`/residents/${id}/family`, famForm);
      if (r.data.is_new) {
        setNewFamCredentials({ email: r.data.email, password: r.data.temp_password });
        toast.success("Familiar creado con credenciales temporales");
      } else {
        toast.success("Familiar vinculado correctamente");
        setFamDialog(false);
      }
      setFamForm({ name: "", email: "", phone: "", relationship: "hijo" });
      const fam = await api.get(`/residents/${id}/family`);
      setFamilyMembers(fam.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Error al agregar familiar"); }
  };

  const handleRemoveFamily = async (linkId) => {
    try {
      await api.delete(`/residents/${id}/family/${linkId}`);
      toast.success("Familiar desvinculado");
      const fam = await api.get(`/residents/${id}/family`);
      setFamilyMembers(fam.data);
    } catch { toast.error("Error"); }
  };

  // ==================== HC HANDLERS ====================
  const handleAddDiagnosis = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/residents/${id}/diagnoses`, diagForm);
      toast.success("Diagnóstico agregado");
      setHcDialog(null);
      setDiagForm({ name: "", icd_code: "", diagnosis_date: "", status: "activo", notes: "" });
      const r = await api.get(`/residents/${id}/diagnoses`);
      setDiagnoses(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handleAddAllergy = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/residents/${id}/allergies`, allergyForm);
      toast.success("Alergia registrada");
      setHcDialog(null);
      setAllergyForm({ allergen: "", allergy_type: "medicamentosa", severity: "moderada", reaction: "", notes: "" });
      const r = await api.get(`/residents/${id}/allergies`);
      setAllergies(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handleAddSurgery = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/residents/${id}/surgical-history`, surgeryForm);
      toast.success("Antecedente registrado");
      setHcDialog(null);
      setSurgeryForm({ procedure: "", date: "", hospital: "", surgeon: "", notes: "" });
      const r = await api.get(`/residents/${id}/surgical-history`);
      setSurgicalHistory(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handleLinkDoctor = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/residents/${id}/doctors`, linkDoctorForm);
      toast.success("Médico vinculado");
      setHcDialog(null);
      setLinkDoctorForm({ doctor_id: "", role: "especialista" });
      const r = await api.get(`/residents/${id}/doctors`);
      setResidentDoctors(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handleRemoveDoctor = async (linkId) => {
    try {
      await api.delete(`/residents/${id}/doctors/${linkId}`);
      toast.success("Médico desvinculado");
      const r = await api.get(`/residents/${id}/doctors`);
      setResidentDoctors(r.data);
    } catch { toast.error("Error"); }
  };

  const handleDeleteDiagnosis = async (diagId) => {
    try {
      await api.delete(`/residents/${id}/diagnoses/${diagId}`);
      setDiagnoses(prev => prev.filter(d => d.id !== diagId));
      toast.success("Eliminado");
    } catch { toast.error("Error"); }
  };

  const handleDeleteAllergy = async (allergyId) => {
    try {
      await api.delete(`/residents/${id}/allergies/${allergyId}`);
      setAllergies(prev => prev.filter(a => a.id !== allergyId));
      toast.success("Eliminado");
    } catch { toast.error("Error"); }
  };

  const handleDeleteSurgery = async (shId) => {
    try {
      await api.delete(`/residents/${id}/surgical-history/${shId}`);
      setSurgicalHistory(prev => prev.filter(s => s.id !== shId));
      toast.success("Eliminado");
    } catch { toast.error("Error"); }
  };

  const openEditDialog = () => {
    setEditForm({
      name: resident.name || "",
      dni: resident.dni || "",
      room_number: resident.room_number || "",
      obra_social: resident.obra_social || "PAMI",
      dependency_level: resident.dependency_level || "autovalido",
      fecha_nacimiento: resident.fecha_nacimiento || "",
      fecha_ingreso: resident.fecha_ingreso || "",
      numero_afiliado: resident.numero_afiliado || "",
      emergency_contact_name: resident.emergency_contact_name || "",
      emergency_contact_phone: resident.emergency_contact_phone || "",
      clinical_notes: resident.clinical_notes || "",
    });
    setEditDialog(true);
  };

  const handleEditResident = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/residents/${id}`, editForm);
      const r = await api.get(`/residents/${id}`);
      setResident(r.data);
      setEditDialog(false);
      toast.success("Residente actualizado");
    } catch (err) { toast.error(err.response?.data?.detail || "Error al actualizar"); }
  };

  if (loading) return <p className="text-slate-400 text-center py-12">Cargando...</p>;
  if (!resident) return <p className="text-slate-400 text-center py-12">Residente no encontrado</p>;

  const dep = depColors[resident.dependency_level] || depColors.autovalido;
  const katz = resident.katz_details || {};
  const lawton = resident.lawton_details || {};
  const typeColors = { cronico: "bg-violet-50 text-violet-700", sos: "bg-amber-50 text-amber-700", psicotropico: "bg-fuchsia-50 text-fuchsia-700" };

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="resident-detail-page">
      <Button variant="ghost" onClick={() => navigate("/residents")} className="rounded-xl text-slate-400 hover:text-slate-700 pl-2 text-sm" data-testid="back-to-residents-btn">
        <ArrowLeft className="w-4 h-4 mr-2" /> Volver a Residentes
      </Button>

      <Card className="rounded-2xl border-slate-100 shadow-sm">
        <CardContent className="p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:items-center gap-5">
            <div className="w-16 h-16 rounded-2xl bg-violet-50 flex items-center justify-center text-violet-600 font-bold text-2xl flex-shrink-0">
              {resident.name?.charAt(0)}
            </div>
            <div className="flex-1">
              <h1 className="text-xl md:text-2xl font-bold text-slate-900 tracking-tight font-[Manrope]">{resident.name}</h1>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <Badge className={`rounded-full px-3 py-1 font-semibold text-xs ${dep.bg} ${dep.text} border-0`}>{dep.label}</Badge>
                <span className="text-sm text-slate-400 flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> DNI: {resident.dni}</span>
                {resident.room_number && <span className="text-sm text-slate-400 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Hab. {resident.room_number}</span>}
                <span className="text-sm text-slate-400 flex items-center gap-1"><Heart className="w-3.5 h-3.5" /> {resident.obra_social}</span>
              </div>
              {resident.fecha_nacimiento && <p className="text-xs text-slate-400 mt-1.5 flex items-center gap-1"><Calendar className="w-3 h-3" /> Nacimiento: {resident.fecha_nacimiento}</p>}
              {resident.fecha_ingreso && <p className="text-xs text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" /> Ingreso: {resident.fecha_ingreso}</p>}
              {resident.numero_afiliado && <p className="text-xs text-slate-400">N° Afiliado: {resident.numero_afiliado}</p>}
              {resident.emergency_contact_name && (
                <p className="text-xs text-slate-400 mt-1">Emergencia: {resident.emergency_contact_name} {resident.emergency_contact_phone ? `— ${resident.emergency_contact_phone}` : ""}</p>
              )}
              {resident.access_code && (
                <p className="text-xs text-slate-400 mt-1">Portal Familiar: <span className="font-mono font-semibold text-slate-700">{resident.access_code}</span></p>
              )}
            </div>
            {canManage && (
              <Button onClick={openEditDialog} variant="outline" className="rounded-xl h-9 px-4 text-sm font-semibold border-slate-200 text-slate-600 hover:bg-slate-50" data-testid="edit-resident-btn">
                <Pencil className="w-3.5 h-3.5 mr-1.5" /> Editar
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Edit Resident Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="rounded-2xl sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-bold text-slate-900 font-[Manrope]">Editar Residente</DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">Modificar datos personales de {resident?.name}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditResident} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label className="text-slate-700 font-medium text-sm">Nombre Completo</Label>
              <Input value={editForm.name || ""} onChange={e => setEditForm({...editForm, name: e.target.value})} className="rounded-xl h-10 bg-slate-50 border-slate-200" required data-testid="edit-resident-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium text-sm">DNI</Label>
                <Input value={editForm.dni || ""} onChange={e => setEditForm({...editForm, dni: e.target.value})} className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="edit-resident-dni" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium text-sm">Habitación</Label>
                <Input value={editForm.room_number || ""} onChange={e => setEditForm({...editForm, room_number: e.target.value})} className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="edit-resident-room" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium text-sm">Fecha de Nacimiento</Label>
                <Input type="date" value={editForm.fecha_nacimiento || ""} onChange={e => setEditForm({...editForm, fecha_nacimiento: e.target.value})} className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="edit-resident-birth" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium text-sm">Fecha de Ingreso</Label>
                <Input type="date" value={editForm.fecha_ingreso || ""} onChange={e => setEditForm({...editForm, fecha_ingreso: e.target.value})} className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="edit-resident-admission" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium text-sm">Obra Social</Label>
                <Select value={editForm.obra_social || "PAMI"} onValueChange={v => setEditForm({...editForm, obra_social: v})}>
                  <SelectTrigger className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="edit-resident-obra"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PAMI">PAMI</SelectItem>
                    <SelectItem value="IOMA">IOMA</SelectItem>
                    <SelectItem value="OSDE">OSDE</SelectItem>
                    <SelectItem value="Swiss Medical">Swiss Medical</SelectItem>
                    <SelectItem value="Otra">Otra</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium text-sm">N° Afiliado</Label>
                <Input value={editForm.numero_afiliado || ""} onChange={e => setEditForm({...editForm, numero_afiliado: e.target.value})} className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="edit-resident-affiliate" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 font-medium text-sm">Dependencia</Label>
              <Select value={editForm.dependency_level || "autovalido"} onValueChange={v => setEditForm({...editForm, dependency_level: v})}>
                <SelectTrigger className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="edit-resident-dep"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="autovalido">Autoválido</SelectItem>
                  <SelectItem value="dependencia_media">Dependencia Media</SelectItem>
                  <SelectItem value="dependencia_total">Dependencia Total</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="border-t border-slate-100 pt-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Contacto de Emergencia</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-slate-700 font-medium text-sm">Nombre</Label>
                  <Input value={editForm.emergency_contact_name || ""} onChange={e => setEditForm({...editForm, emergency_contact_name: e.target.value})} className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="edit-resident-emer-name" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-700 font-medium text-sm">Teléfono</Label>
                  <Input value={editForm.emergency_contact_phone || ""} onChange={e => setEditForm({...editForm, emergency_contact_phone: e.target.value})} className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="edit-resident-emer-phone" />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 font-medium text-sm">Notas Clínicas</Label>
              <Textarea value={editForm.clinical_notes || ""} onChange={e => setEditForm({...editForm, clinical_notes: e.target.value})} className="rounded-xl bg-slate-50 min-h-[60px] border-slate-200 text-sm" data-testid="edit-resident-notes" />
            </div>
            <Button type="submit" className="w-full rounded-xl h-10 bg-violet-600 text-white font-semibold hover:bg-violet-700 active:scale-[0.98] transition-all" data-testid="save-edit-resident-btn">Guardar Cambios</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Tabs defaultValue="clinical" className="w-full">
        <TabsList className="bg-white rounded-xl p-1 border border-slate-100 shadow-sm w-full sm:w-auto h-auto" data-testid="resident-tabs">
          <TabsTrigger value="clinical" className="rounded-lg text-sm font-medium data-[state=active]:bg-violet-600 data-[state=active]:text-white px-5 py-2">Clínico</TabsTrigger>
          <TabsTrigger value="historia" className="rounded-lg text-sm font-medium data-[state=active]:bg-violet-600 data-[state=active]:text-white px-5 py-2">Historia Clínica</TabsTrigger>
          <TabsTrigger value="prescriptions" className="rounded-lg text-sm font-medium data-[state=active]:bg-violet-600 data-[state=active]:text-white px-5 py-2">Prescripciones</TabsTrigger>
          <TabsTrigger value="evolutions" className="rounded-lg text-sm font-medium data-[state=active]:bg-violet-600 data-[state=active]:text-white px-5 py-2">Evoluciones</TabsTrigger>
          <TabsTrigger value="daily-care" className="rounded-lg text-sm font-medium data-[state=active]:bg-violet-600 data-[state=active]:text-white px-5 py-2">Cuidado Diario</TabsTrigger>
          <TabsTrigger value="family" className="rounded-lg text-sm font-medium data-[state=active]:bg-violet-600 data-[state=active]:text-white px-5 py-2">Familiares</TabsTrigger>
        </TabsList>

        <TabsContent value="clinical" className="mt-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Card className="rounded-2xl border-slate-100 shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-slate-900 text-sm">Escala de Katz</h3>
                  <Badge className="rounded-full bg-slate-100 text-slate-700 font-bold border-0 text-xs">{resident.katz_score}/6</Badge>
                </div>
                <p className="text-[10px] text-slate-400 uppercase tracking-widest mb-3 font-semibold">Actividades Básicas de la Vida Diaria</p>
                <div className="space-y-1">
                  {katzItems.map(item => (
                    <div key={item.key} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                      <span className="text-sm text-slate-700">{item.label}</span>
                      <Badge className={`rounded-full text-[10px] font-semibold border-0 px-2.5 ${katz[item.key] === 0 ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-600"}`}>
                        {katz[item.key] === 0 ? "Independiente" : "Dependiente"}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            <Card className="rounded-2xl border-slate-100 shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-slate-900 text-sm">Escala de Lawton & Brody</h3>
                  <Badge className="rounded-full bg-slate-100 text-slate-700 font-bold border-0 text-xs">{resident.lawton_score}/8</Badge>
                </div>
                <p className="text-[10px] text-slate-400 uppercase tracking-widest mb-3 font-semibold">Actividades Instrumentales</p>
                <div className="space-y-1">
                  {lawtonItems.map(item => (
                    <div key={item.key} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                      <span className="text-sm text-slate-700">{item.label}</span>
                      <Badge className={`rounded-full text-[10px] font-semibold border-0 px-2.5 ${lawton[item.key] === 1 ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-600"}`}>
                        {lawton[item.key] === 1 ? "Independiente" : "Dependiente"}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
          <Card className="rounded-2xl border-slate-100 shadow-sm">
            <CardContent className="p-5">
              <h3 className="font-semibold text-slate-900 text-sm mb-2">Notas Clínicas</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{resident.clinical_notes || "Sin notas clínicas registradas."}</p>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border-slate-100 shadow-sm">
            <CardContent className="p-5">
              <h3 className="font-semibold text-slate-900 text-sm mb-4">Información Personal</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[
                  { label: "Fecha Nac.", value: resident.fecha_nacimiento },
                  { label: "Fecha Ingreso", value: resident.fecha_ingreso },
                  { label: "N° Afiliado", value: resident.numero_afiliado },
                  { label: "Contacto Emergencia", value: resident.emergency_contact_name },
                  { label: "Tel. Emergencia", value: resident.emergency_contact_phone },
                ].map(f => (
                  <div key={f.label}>
                    <p className="text-[10px] text-slate-400 uppercase tracking-widest font-semibold">{f.label}</p>
                    <p className="text-sm text-slate-700 font-medium mt-1">{f.value || "—"}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>


        {/* ==================== HISTORIA CLINICA TAB ==================== */}
        <TabsContent value="historia" className="mt-6 space-y-5" data-testid="historia-clinica-tab">
          {/* Médicos Vinculados */}
          <Card className="rounded-2xl border-slate-100 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-900 text-sm">Equipo Médico</h3>
                {canManage && allDoctors.length > 0 && (
                  <Button size="sm" variant="outline" onClick={() => setHcDialog("doctor")} className="rounded-xl text-xs border-slate-200 h-8" data-testid="link-doctor-btn">
                    <Plus className="w-3 h-3 mr-1" /> Vincular Médico
                  </Button>
                )}
              </div>
              {residentDoctors.length === 0 ? (
                <p className="text-xs text-slate-400">No hay médicos vinculados</p>
              ) : (
                <div className="space-y-2">
                  {residentDoctors.map(rd => (
                    <div key={rd.link_id} className="flex items-center justify-between bg-slate-50 rounded-xl p-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${rd.role === "cabecera" ? "bg-violet-100" : "bg-sky-50"}`}>
                          <span className="text-xs font-bold text-slate-600">{rd.role === "cabecera" ? "MC" : "E"}</span>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-slate-800">{rd.name}</p>
                          <span className="text-[10px] text-slate-400">{rd.specialty} — {rd.matricula} {rd.role === "cabecera" && <Badge className="rounded-full bg-violet-100 text-violet-700 border-0 text-[9px] ml-1">Cabecera</Badge>}</span>
                        </div>
                      </div>
                      {canManage && (
                        <Button variant="ghost" size="icon" onClick={() => handleRemoveDoctor(rd.link_id)} className="w-7 h-7 rounded-full hover:bg-rose-50 hover:text-rose-500">
                          <X className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Diagnósticos */}
          <Card className="rounded-2xl border-slate-100 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-900 text-sm">Diagnósticos ({diagnoses.length})</h3>
                {canManage && (
                  <Button size="sm" variant="outline" onClick={() => setHcDialog("diagnosis")} className="rounded-xl text-xs border-slate-200 h-8" data-testid="add-diagnosis-btn">
                    <Plus className="w-3 h-3 mr-1" /> Agregar
                  </Button>
                )}
              </div>
              {diagnoses.length === 0 ? (
                <p className="text-xs text-slate-400">Sin diagnósticos registrados</p>
              ) : (
                <div className="space-y-2">
                  {diagnoses.map(d => (
                    <div key={d.id} className="flex items-start justify-between bg-slate-50 rounded-xl p-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-slate-800">{d.name}</p>
                          <Badge className={`rounded-full text-[10px] border-0 ${d.status === "activo" ? "bg-emerald-50 text-emerald-700" : d.status === "cronico" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-500"}`}>
                            {d.status}
                          </Badge>
                        </div>
                        <p className="text-[10px] text-slate-400 mt-0.5">
                          {d.icd_code && <span className="font-mono mr-2">{d.icd_code}</span>}
                          {d.diagnosis_date && `Desde: ${d.diagnosis_date}`}
                          {d.notes && ` — ${d.notes}`}
                        </p>
                      </div>
                      {canManage && (
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteDiagnosis(d.id)} className="w-7 h-7 rounded-full hover:bg-rose-50 hover:text-rose-500 flex-shrink-0">
                          <X className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Alergias */}
          <Card className="rounded-2xl border-slate-100 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-900 text-sm">Alergias ({allergies.length})</h3>
                {canManage && (
                  <Button size="sm" variant="outline" onClick={() => setHcDialog("allergy")} className="rounded-xl text-xs border-slate-200 h-8" data-testid="add-allergy-btn">
                    <Plus className="w-3 h-3 mr-1" /> Agregar
                  </Button>
                )}
              </div>
              {allergies.length === 0 ? (
                <p className="text-xs text-slate-400">Sin alergias registradas</p>
              ) : (
                <div className="space-y-2">
                  {allergies.map(a => (
                    <div key={a.id} className="flex items-start justify-between bg-slate-50 rounded-xl p-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-slate-800">{a.allergen}</p>
                          <Badge className={`rounded-full text-[10px] border-0 ${a.severity === "grave" ? "bg-rose-50 text-rose-700" : a.severity === "moderada" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-500"}`}>
                            {a.severity}
                          </Badge>
                          <Badge className="rounded-full text-[10px] bg-slate-100 text-slate-500 border-0">{a.allergy_type}</Badge>
                        </div>
                        {a.reaction && <p className="text-[10px] text-slate-400 mt-0.5">Reacción: {a.reaction}</p>}
                      </div>
                      {canManage && (
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteAllergy(a.id)} className="w-7 h-7 rounded-full hover:bg-rose-50 hover:text-rose-500 flex-shrink-0">
                          <X className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Antecedentes Quirúrgicos */}
          <Card className="rounded-2xl border-slate-100 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-900 text-sm">Antecedentes Quirúrgicos ({surgicalHistory.length})</h3>
                {canManage && (
                  <Button size="sm" variant="outline" onClick={() => setHcDialog("surgery")} className="rounded-xl text-xs border-slate-200 h-8" data-testid="add-surgery-btn">
                    <Plus className="w-3 h-3 mr-1" /> Agregar
                  </Button>
                )}
              </div>
              {surgicalHistory.length === 0 ? (
                <p className="text-xs text-slate-400">Sin antecedentes quirúrgicos</p>
              ) : (
                <div className="space-y-2">
                  {surgicalHistory.map(s => (
                    <div key={s.id} className="flex items-start justify-between bg-slate-50 rounded-xl p-3">
                      <div>
                        <p className="text-sm font-semibold text-slate-800">{s.procedure}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">
                          {s.date && `Fecha: ${s.date}`}{s.hospital && ` — ${s.hospital}`}{s.surgeon && ` — ${s.surgeon}`}
                        </p>
                      </div>
                      {canManage && (
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteSurgery(s.id)} className="w-7 h-7 rounded-full hover:bg-rose-50 hover:text-rose-500 flex-shrink-0">
                          <X className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* ==================== HC DIALOGS ==================== */}
          <Dialog open={hcDialog === "doctor"} onOpenChange={v => { if (!v) setHcDialog(null); }}>
            <DialogContent className="rounded-2xl sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-bold text-slate-900 font-[Manrope]">Vincular Médico</DialogTitle>
                <DialogDescription className="text-slate-400 text-sm">Seleccioná un médico del catálogo</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleLinkDoctor} className="space-y-4 mt-2">
                <div className="space-y-2">
                  <Label className="font-medium text-sm text-slate-700">Médico</Label>
                  <Select value={linkDoctorForm.doctor_id} onValueChange={v => setLinkDoctorForm({ ...linkDoctorForm, doctor_id: v })}>
                    <SelectTrigger className="rounded-xl h-10 bg-slate-50"><SelectValue placeholder="Seleccionar médico..." /></SelectTrigger>
                    <SelectContent>
                      {allDoctors.filter(d => !residentDoctors.some(rd => rd.doctor_id === d.id)).map(d => (
                        <SelectItem key={d.id} value={d.id}>{d.name} — {d.specialty}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="font-medium text-sm text-slate-700">Rol</Label>
                  <Select value={linkDoctorForm.role} onValueChange={v => setLinkDoctorForm({ ...linkDoctorForm, role: v })}>
                    <SelectTrigger className="rounded-xl h-10 bg-slate-50"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cabecera">Médico de Cabecera</SelectItem>
                      <SelectItem value="especialista">Especialista</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" disabled={!linkDoctorForm.doctor_id} className="w-full rounded-xl h-10 bg-violet-600 text-white font-semibold hover:bg-violet-700">Vincular</Button>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={hcDialog === "diagnosis"} onOpenChange={v => { if (!v) setHcDialog(null); }}>
            <DialogContent className="rounded-2xl sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-bold text-slate-900 font-[Manrope]">Nuevo Diagnóstico</DialogTitle>
                <DialogDescription className="text-slate-400 text-sm">Registrar diagnóstico del residente</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddDiagnosis} className="space-y-4 mt-2">
                <div className="space-y-2">
                  <Label className="font-medium text-sm text-slate-700">Diagnóstico</Label>
                  <Input value={diagForm.name} onChange={e => setDiagForm({ ...diagForm, name: e.target.value })} placeholder="Ej: Hipertensión arterial" className="rounded-xl h-10 bg-slate-50" required data-testid="diagnosis-name-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="font-medium text-sm text-slate-700">Código CIE-10</Label>
                    <Input value={diagForm.icd_code} onChange={e => setDiagForm({ ...diagForm, icd_code: e.target.value })} placeholder="I10" className="rounded-xl h-10 bg-slate-50" />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-medium text-sm text-slate-700">Fecha diagnóstico</Label>
                    <Input type="date" value={diagForm.diagnosis_date} onChange={e => setDiagForm({ ...diagForm, diagnosis_date: e.target.value })} className="rounded-xl h-10 bg-slate-50" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="font-medium text-sm text-slate-700">Estado</Label>
                  <Select value={diagForm.status} onValueChange={v => setDiagForm({ ...diagForm, status: v })}>
                    <SelectTrigger className="rounded-xl h-10 bg-slate-50"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="activo">Activo</SelectItem>
                      <SelectItem value="cronico">Crónico</SelectItem>
                      <SelectItem value="resuelto">Resuelto</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="font-medium text-sm text-slate-700">Notas</Label>
                  <Textarea value={diagForm.notes} onChange={e => setDiagForm({ ...diagForm, notes: e.target.value })} className="rounded-xl bg-slate-50 min-h-[60px]" />
                </div>
                <Button type="submit" className="w-full rounded-xl h-10 bg-violet-600 text-white font-semibold hover:bg-violet-700">Guardar</Button>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={hcDialog === "allergy"} onOpenChange={v => { if (!v) setHcDialog(null); }}>
            <DialogContent className="rounded-2xl sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-bold text-slate-900 font-[Manrope]">Nueva Alergia</DialogTitle>
                <DialogDescription className="text-slate-400 text-sm">Registrar alergia del residente</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddAllergy} className="space-y-4 mt-2">
                <div className="space-y-2">
                  <Label className="font-medium text-sm text-slate-700">Alérgeno</Label>
                  <Input value={allergyForm.allergen} onChange={e => setAllergyForm({ ...allergyForm, allergen: e.target.value })} placeholder="Ej: Penicilina" className="rounded-xl h-10 bg-slate-50" required data-testid="allergy-allergen-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="font-medium text-sm text-slate-700">Tipo</Label>
                    <Select value={allergyForm.allergy_type} onValueChange={v => setAllergyForm({ ...allergyForm, allergy_type: v })}>
                      <SelectTrigger className="rounded-xl h-10 bg-slate-50"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="medicamentosa">Medicamentosa</SelectItem>
                        <SelectItem value="alimentaria">Alimentaria</SelectItem>
                        <SelectItem value="ambiental">Ambiental</SelectItem>
                        <SelectItem value="otra">Otra</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="font-medium text-sm text-slate-700">Severidad</Label>
                    <Select value={allergyForm.severity} onValueChange={v => setAllergyForm({ ...allergyForm, severity: v })}>
                      <SelectTrigger className="rounded-xl h-10 bg-slate-50"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="leve">Leve</SelectItem>
                        <SelectItem value="moderada">Moderada</SelectItem>
                        <SelectItem value="grave">Grave</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="font-medium text-sm text-slate-700">Reacción</Label>
                  <Input value={allergyForm.reaction} onChange={e => setAllergyForm({ ...allergyForm, reaction: e.target.value })} placeholder="Ej: Erupción cutánea" className="rounded-xl h-10 bg-slate-50" />
                </div>
                <Button type="submit" className="w-full rounded-xl h-10 bg-violet-600 text-white font-semibold hover:bg-violet-700">Guardar</Button>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={hcDialog === "surgery"} onOpenChange={v => { if (!v) setHcDialog(null); }}>
            <DialogContent className="rounded-2xl sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-bold text-slate-900 font-[Manrope]">Nuevo Antecedente Quirúrgico</DialogTitle>
                <DialogDescription className="text-slate-400 text-sm">Registrar cirugía o procedimiento</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddSurgery} className="space-y-4 mt-2">
                <div className="space-y-2">
                  <Label className="font-medium text-sm text-slate-700">Procedimiento</Label>
                  <Input value={surgeryForm.procedure} onChange={e => setSurgeryForm({ ...surgeryForm, procedure: e.target.value })} placeholder="Ej: Reemplazo de cadera" className="rounded-xl h-10 bg-slate-50" required data-testid="surgery-procedure-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="font-medium text-sm text-slate-700">Fecha</Label>
                    <Input type="date" value={surgeryForm.date} onChange={e => setSurgeryForm({ ...surgeryForm, date: e.target.value })} className="rounded-xl h-10 bg-slate-50" />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-medium text-sm text-slate-700">Hospital</Label>
                    <Input value={surgeryForm.hospital} onChange={e => setSurgeryForm({ ...surgeryForm, hospital: e.target.value })} className="rounded-xl h-10 bg-slate-50" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="font-medium text-sm text-slate-700">Cirujano</Label>
                  <Input value={surgeryForm.surgeon} onChange={e => setSurgeryForm({ ...surgeryForm, surgeon: e.target.value })} className="rounded-xl h-10 bg-slate-50" />
                </div>
                <Button type="submit" className="w-full rounded-xl h-10 bg-violet-600 text-white font-semibold hover:bg-violet-700">Guardar</Button>
              </form>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="prescriptions" className="mt-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-slate-900 text-sm">{prescriptions.length} prescripciones activas</h3>
            {canManage && (
              <Dialog open={rxDialog} onOpenChange={setRxDialog}>
                <DialogTrigger asChild>
                  <Button className="rounded-xl px-5 h-9 bg-violet-600 text-white hover:bg-violet-700 active:scale-[0.98] transition-all text-sm font-semibold" data-testid="add-prescription-btn">
                    <Plus className="w-4 h-4 mr-2" /> Nueva Prescripción
                  </Button>
                </DialogTrigger>
              <DialogContent className="rounded-2xl sm:max-w-md">
                <DialogHeader><DialogTitle className="font-bold text-slate-900 font-[Manrope]">Nueva Prescripción</DialogTitle><DialogDescription className="text-slate-400 text-sm">Agregar una nueva prescripción al residente</DialogDescription></DialogHeader>
                <form onSubmit={handleAddRx} className="space-y-4 mt-2">
                  <div className="space-y-2">
                    <Label className="font-medium text-sm text-slate-700">Medicamento</Label>
                    <Input value={rxForm.medication_name} onChange={e => setRxForm({...rxForm, medication_name: e.target.value})} className="rounded-xl h-10 bg-slate-50 border-slate-200" required data-testid="rx-medication-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="font-medium text-sm text-slate-700">Dosis</Label>
                      <Input value={rxForm.dosage} onChange={e => setRxForm({...rxForm, dosage: e.target.value})} className="rounded-xl h-10 bg-slate-50 border-slate-200" required data-testid="rx-dosage-input" />
                    </div>
                    <div className="space-y-2">
                      <Label className="font-medium text-sm text-slate-700">Vía</Label>
                      <Select value={rxForm.route} onValueChange={v => setRxForm({...rxForm, route: v})}>
                        <SelectTrigger className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="rx-route-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="oral">Oral</SelectItem>
                          <SelectItem value="sublingual">Sublingual</SelectItem>
                          <SelectItem value="intramuscular">Intramuscular</SelectItem>
                          <SelectItem value="subcutanea">Subcutánea</SelectItem>
                          <SelectItem value="topica">Tópica</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="font-medium text-sm text-slate-700">Tipo</Label>
                      <Select value={rxForm.type} onValueChange={v => setRxForm({...rxForm, type: v})}>
                        <SelectTrigger className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="rx-type-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cronico">Crónico</SelectItem>
                          <SelectItem value="sos">SOS (Rescate)</SelectItem>
                          <SelectItem value="psicotropico">Psicotrópico</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="font-medium text-sm text-slate-700">Horarios</Label>
                      <Input value={rxForm.scheduled_times} onChange={e => setRxForm({...rxForm, scheduled_times: e.target.value})} placeholder="08:00, 20:00" className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="rx-times-input" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="font-medium text-sm text-slate-700">Médico Prescriptor</Label>
                    <Input value={rxForm.prescribing_doctor} onChange={e => setRxForm({...rxForm, prescribing_doctor: e.target.value})} className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="rx-doctor-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-medium text-sm text-slate-700">Notas</Label>
                    <Textarea value={rxForm.notes} onChange={e => setRxForm({...rxForm, notes: e.target.value})} className="rounded-xl bg-slate-50 min-h-[60px] border-slate-200" data-testid="rx-notes-input" />
                  </div>
                  <Button type="submit" className="w-full rounded-xl h-10 bg-violet-600 text-white font-semibold hover:bg-violet-700 active:scale-[0.98] transition-all" data-testid="submit-rx-btn">Crear Prescripción</Button>
                </form>
              </DialogContent>
            </Dialog>
            )}
          </div>
          {prescriptions.length === 0 ? (
            <p className="text-slate-400 text-center py-8">Sin prescripciones activas</p>
          ) : (
            <div className="space-y-3">
              {prescriptions.map(rx => (
                <Card key={rx.id} className="rounded-2xl border-slate-100 shadow-sm" data-testid={`prescription-${rx.id}`}>
                  <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center flex-shrink-0">
                      <Pill className="w-5 h-5 text-slate-400" strokeWidth={1.8} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-semibold text-slate-900 text-sm">{rx.medication_name}</h4>
                        <Badge className={`rounded-full text-[10px] font-semibold border-0 ${typeColors[rx.type] || "bg-slate-100 text-slate-600"}`}>
                          {rx.type === "cronico" ? "Crónico" : rx.type === "sos" ? "SOS" : "Psicotrópico"}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-400 mt-0.5">{rx.dosage} — {rx.route}</p>
                      {rx.scheduled_times?.length > 0 && (
                        <div className="flex items-center gap-1 mt-1">
                          <Clock className="w-3 h-3 text-slate-300" />
                          <span className="text-xs text-slate-400">{rx.scheduled_times.join(", ")}</span>
                        </div>
                      )}
                    </div>
                    {rx.prescribing_doctor && <span className="text-xs text-slate-400">{rx.prescribing_doctor}</span>}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="evolutions" className="mt-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-slate-900 text-sm">{evolutions.length} evolución(es) registrada(s)</h3>
            {canManage && (
              <Dialog open={evoDialog} onOpenChange={setEvoDialog}>
                <DialogTrigger asChild>
                  <Button className="rounded-xl px-5 h-9 bg-violet-600 text-white hover:bg-violet-700 active:scale-[0.98] transition-all text-sm font-semibold" data-testid="add-evolution-btn">
                    <Plus className="w-4 h-4 mr-2" /> Nueva Evolución
                  </Button>
                </DialogTrigger>
                <DialogContent className="rounded-2xl sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle className="font-bold text-slate-900 font-[Manrope]">Registrar Evolución</DialogTitle>
                    <DialogDescription className="text-slate-400 text-sm">Evolución de enfermería del residente</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleAddEvolution} className="space-y-4 mt-2">
                    <div className="space-y-2">
                      <Label className="font-medium text-sm text-slate-700">Categoría</Label>
                      <Select value={evoForm.category} onValueChange={v => setEvoForm({ ...evoForm, category: v })}>
                        <SelectTrigger className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="evo-category-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">General</SelectItem>
                          <SelectItem value="estado_general">Estado General</SelectItem>
                          <SelectItem value="medicacion">Medicación</SelectItem>
                          <SelectItem value="alimentacion">Alimentación</SelectItem>
                          <SelectItem value="higiene">Higiene</SelectItem>
                          <SelectItem value="movilidad">Movilidad</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="font-medium text-sm text-slate-700">Evolución</Label>
                      <Textarea
                        value={evoForm.content}
                        onChange={e => setEvoForm({ ...evoForm, content: e.target.value })}
                        placeholder="Describir la evolución del residente..."
                        className="rounded-xl bg-slate-50 min-h-[100px] border-slate-200"
                        required
                        data-testid="evo-content-input"
                      />
                    </div>
                    <Button type="submit" disabled={!evoForm.content.trim()} className="w-full rounded-xl h-10 bg-violet-600 text-white font-semibold hover:bg-violet-700 active:scale-[0.98] transition-all" data-testid="submit-evo-btn">
                      Registrar Evolución
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
          {evolutions.length === 0 ? (
            <p className="text-slate-400 text-center py-8">Sin evoluciones registradas</p>
          ) : (
            <div className="space-y-3">
              {evolutions.map(evo => {
                const catLabels = { general: "General", estado_general: "Estado General", medicacion: "Medicación", alimentacion: "Alimentación", higiene: "Higiene", movilidad: "Movilidad" };
                const catColors = { general: "bg-slate-50 text-slate-600", estado_general: "bg-violet-50 text-violet-700", medicacion: "bg-fuchsia-50 text-fuchsia-700", alimentacion: "bg-amber-50 text-amber-700", higiene: "bg-sky-50 text-sky-700", movilidad: "bg-emerald-50 text-emerald-700" };
                return (
                  <Card key={evo.id} className="rounded-2xl border-slate-100 shadow-sm" data-testid={`evolution-${evo.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Stethoscope className="w-4 h-4 text-slate-300" />
                        <Badge className={`rounded-full text-[10px] font-semibold border-0 px-2.5 ${catColors[evo.category] || catColors.general}`}>
                          {catLabels[evo.category] || evo.category}
                        </Badge>
                        <span className="text-xs text-slate-400">{new Date(evo.created_at).toLocaleString("es-AR")}</span>
                        <span className="text-xs text-slate-400">— {evo.created_by_name}</span>
                      </div>
                      <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{evo.content}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="daily-care" className="mt-6 space-y-4" data-testid="daily-care-tab">
          <DailyCareChecklist
            residentId={id}
            todayStr={todayStr}
            dailyCare={dailyCare}
            onUpdate={(records) => setDailyCare(records)}
            canManage={canManage || user?.permissions?.includes("cargar_registros")}
          />
        </TabsContent>

        <TabsContent value="family" className="mt-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-slate-900 text-sm">{familyMembers.length} familiar(es) vinculado(s)</h3>
            {canManage && (
              <Dialog open={famDialog} onOpenChange={v => { setFamDialog(v); if (!v) setNewFamCredentials(null); }}>
                <DialogTrigger asChild>
                  <Button className="rounded-xl px-5 h-9 bg-violet-600 text-white hover:bg-violet-700 active:scale-[0.98] transition-all text-sm font-semibold" data-testid="add-family-btn">
                    <Plus className="w-4 h-4 mr-2" /> Agregar Familiar
                  </Button>
                </DialogTrigger>
                <DialogContent className="rounded-2xl sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle className="font-bold text-slate-900 font-[Manrope]">Agregar Familiar</DialogTitle>
                    <DialogDescription className="text-slate-400 text-sm">Crear cuenta de acceso para un familiar de {resident?.name}</DialogDescription>
                  </DialogHeader>
                  {newFamCredentials ? (
                    <div className="space-y-4 mt-2">
                      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                        <p className="text-sm font-semibold text-emerald-700 mb-2">Cuenta creada correctamente</p>
                        <p className="text-sm text-emerald-600">Compartí estas credenciales con el familiar:</p>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-[10px] text-slate-400 uppercase tracking-widest font-semibold">Email</p>
                            <p className="text-sm font-mono font-semibold text-slate-900">{newFamCredentials.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-[10px] text-slate-400 uppercase tracking-widest font-semibold">Contraseña temporal</p>
                            <p className="text-sm font-mono font-semibold text-slate-900">{newFamCredentials.password}</p>
                          </div>
                        </div>
                      </div>
                      <Button onClick={() => { setFamDialog(false); setNewFamCredentials(null); }} className="w-full rounded-xl h-10 bg-violet-600 text-white font-semibold hover:bg-violet-700" data-testid="close-fam-credentials">
                        Entendido
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleAddFamily} className="space-y-4 mt-2">
                      <div className="space-y-2">
                        <Label className="font-medium text-sm text-slate-700">Nombre completo</Label>
                        <Input value={famForm.name} onChange={e => setFamForm({ ...famForm, name: e.target.value })} className="rounded-xl h-10 bg-slate-50 border-slate-200" required data-testid="fam-name-input" />
                      </div>
                      <div className="space-y-2">
                        <Label className="font-medium text-sm text-slate-700">Email</Label>
                        <Input type="email" value={famForm.email} onChange={e => setFamForm({ ...famForm, email: e.target.value })} className="rounded-xl h-10 bg-slate-50 border-slate-200" required data-testid="fam-email-input" />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label className="font-medium text-sm text-slate-700">Teléfono</Label>
                          <Input value={famForm.phone} onChange={e => setFamForm({ ...famForm, phone: e.target.value })} placeholder="Opcional" className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="fam-phone-input" />
                        </div>
                        <div className="space-y-2">
                          <Label className="font-medium text-sm text-slate-700">Parentesco</Label>
                          <Select value={famForm.relationship} onValueChange={v => setFamForm({ ...famForm, relationship: v })}>
                            <SelectTrigger className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="fam-relationship-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="hijo">Hijo/a</SelectItem>
                              <SelectItem value="esposo">Esposo/a</SelectItem>
                              <SelectItem value="nieto">Nieto/a</SelectItem>
                              <SelectItem value="hermano">Hermano/a</SelectItem>
                              <SelectItem value="sobrino">Sobrino/a</SelectItem>
                              <SelectItem value="otro">Otro</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <p className="text-xs text-slate-400">Se generará una contraseña temporal que deberás compartir con el familiar.</p>
                      <Button type="submit" disabled={!famForm.name || !famForm.email} className="w-full rounded-xl h-10 bg-violet-600 text-white font-semibold hover:bg-violet-700 active:scale-[0.98] transition-all" data-testid="submit-family-btn">
                        Crear Cuenta Familiar
                      </Button>
                    </form>
                  )}
                </DialogContent>
              </Dialog>
            )}
          </div>
          {familyMembers.length === 0 ? (
            <p className="text-slate-400 text-center py-8">Sin familiares vinculados</p>
          ) : (
            <div className="space-y-3">
              {familyMembers.map(fam => {
                const relLabels = { hijo: "Hijo/a", esposo: "Esposo/a", nieto: "Nieto/a", hermano: "Hermano/a", sobrino: "Sobrino/a", otro: "Otro" };
                return (
                  <Card key={fam.link_id} className="rounded-2xl border-slate-100 shadow-sm" data-testid={`family-member-${fam.link_id}`}>
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center text-violet-600 font-bold flex-shrink-0">
                        <Users className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-900 text-sm">{fam.name}</p>
                        <p className="text-xs text-slate-400">{fam.email} {fam.phone ? `· ${fam.phone}` : ""}</p>
                      </div>
                      <Badge className="rounded-full bg-slate-50 text-slate-600 border-0 text-[10px] font-semibold">
                        {relLabels[fam.relationship] || fam.relationship}
                      </Badge>
                      {canManage && (
                        <Button variant="ghost" size="icon" onClick={() => handleRemoveFamily(fam.link_id)} className="rounded-full hover:bg-rose-50 hover:text-rose-500 w-8 h-8" data-testid={`remove-family-${fam.link_id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
